import { keyframes } from "@emotion/react";
import { Box, styled } from "@mui/material";
import { IStyledAssistentButtonProps } from "./types";

const BADGE_SIZE = 44;
const BADGE_FILL_SIZE = BADGE_SIZE * 1.6;
const WAVE_WIDTH = 18;
const WAVE_HEIGHT = 15;
const LABEL_TEXT_WIDTH = 90;

const spin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;

const StyledAssistentButton = styled(Box)<IStyledAssistentButtonProps>(({ theme }) => {
    const palette = (theme as any).palette;
    const white = palette.neutral.lighter;
    const labelGap = theme.spacing(1);
    const labelPaddingRight = theme.spacing(1.75);

    return {
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        borderRadius: 999,
        color: white,
        backgroundColor: "transparent",
        transition: theme.transitions.create(["transform", "background-color"], {
            duration: theme.transitions.duration.short,
        }),
        "@media (prefers-reduced-motion: reduce)": {
            "& .AssistentButton-badge::before": {
                animation: "none",
            },
            "& .AssistentButton-wave": {
                animation: "none",
            },
        },
        "& .MuiIconButton-root": {
            marginRight: labelGap,
        },
        "& .AssistentButton-badge": {
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            width: BADGE_SIZE,
            height: BADGE_SIZE,
            borderRadius: "50%",
            overflow: "hidden",
        },
        "& .AssistentButton-badge::before": {
            content: '""',
            position: "absolute",
            top: "50%",
            left: "50%",
            width: BADGE_FILL_SIZE,
            height: BADGE_FILL_SIZE,
            marginTop: -BADGE_FILL_SIZE / 2,
            marginLeft: -BADGE_FILL_SIZE / 2,
            zIndex: 0,
            background: `conic-gradient(from 0deg, ${palette.primary.light} 0%, ${palette.accentInfo.main} 33%, ${palette.primary.darkest} 66%, ${palette.primary.light} 100%)`,
            animation: `${spin} 9s linear infinite`,
            pointerEvents: "none",
        },
        "& .AssistentButton-wave": {
            position: "relative",
            zIndex: 1,
            display: "block",
            flexShrink: 0,
            width: WAVE_WIDTH,
            height: WAVE_HEIGHT,
            animation: `${spin} 4s linear infinite`,
        },
        "& .AssistentButton-wave svg": {
            display: "block",
            width: "100%",
            height: "100%",
        },
        "& .AssistentButton-wave path": {
            fill: white,
        },
        "& .AssistentButton-label": {
            boxSizing: "content-box",
            display: "inline-block",
            maxWidth: 0,
            marginLeft: 0,
            paddingRight: 0,
            opacity: 0,
            overflow: "hidden",
            whiteSpace: "nowrap",
            color: white,
            fontWeight: 700,
            transition: theme.transitions.create(["max-width", "margin-left", "padding-right", "opacity"], {
                duration: theme.transitions.duration.short,
            }),
        },
        ":hover": {
            cursor: "pointer",
            transform: "translateY(-1px)",
            backgroundColor: palette.neutral.darkest,
        },
        ":hover .AssistentButton-label": {
            maxWidth: LABEL_TEXT_WIDTH,
            marginLeft: labelGap,
            paddingRight: labelPaddingRight,
            opacity: 1,
        },
    };
});

export { StyledAssistentButton };
